// =============================================================================
// features/sales/campaigns/wizard/audience.ts — Funções puras que transformam
// o estado da etapa 2 (`AudienceState`) no `AudienceSource` que a API espera
// (F35-S08). NENHUMA classificação de leads acontece aqui — isso é
// responsabilidade exclusiva de `classifyAudience` no backend (ver aviso em
// hooks/sales/useCampaignWizard.ts). Este módulo só monta e valida o
// payload de entrada.
// =============================================================================

import type { AudienceFilter, AudienceSource } from '@elemento/shared-schemas';

import type { AudienceFilterState, AudienceState } from './types';

export const MAX_MANUAL_LEAD_IDS = 5000;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Um id por linha (aceita vírgula também) — espaços/linhas vazias ignorados. */
export function parseManualLeadIds(raw: string): { ids: string[]; invalidTokens: string[] } {
  const tokens = raw
    .split(/[\n,]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const ids: string[] = [];
  const invalidTokens: string[] = [];
  for (const token of tokens) {
    if (UUID_REGEX.test(token)) {
      ids.push(token.toLowerCase());
    } else {
      invalidTokens.push(token);
    }
  }
  return { ids, invalidTokens };
}

function buildAudienceFilter(state: AudienceFilterState): AudienceFilter {
  const filter: AudienceFilter = {};
  if (state.leadStatus && state.leadStatus.length > 0) filter.leadStatus = state.leadStatus;
  if (state.assignedAgentIds && state.assignedAgentIds.length > 0) {
    filter.assignedAgentIds = state.assignedAgentIds;
  }
  if (state.createdFrom)
    filter.createdFrom = new Date(`${state.createdFrom}T00:00:00Z`).toISOString();
  if (state.createdTo) filter.createdTo = new Date(`${state.createdTo}T23:59:59Z`).toISOString();
  return filter;
}

export interface BuildAudienceSourceResult {
  source: AudienceSource | null;
  /** Motivo pelo qual `source` é `null` — nunca escondido do usuário. */
  blockedReason: string | null;
}

/**
 * Monta o `AudienceSource` a partir do estado da etapa 2. Só cobre
 * `'filter'`/`'manual'` — a fonte `'import'` não tem um `AudienceSource`
 * (usa `POST /members/import` diretamente, ver Step3Hygiene.tsx).
 */
export function buildAudienceSource(state: AudienceState): BuildAudienceSourceResult {
  if (state.kind === 'filter') {
    const filter = buildAudienceFilter(state.filter);
    const hasAnyCriterion = Object.keys(filter).length > 0;
    if (!hasAnyCriterion) {
      return {
        source: null,
        blockedReason: 'Escolha pelo menos um critério de filtro (status, responsável ou período).',
      };
    }
    return { source: { type: 'filter', filter }, blockedReason: null };
  }

  if (state.kind === 'manual') {
    const { ids, invalidTokens } = parseManualLeadIds(state.manualLeadIdsRaw);
    if (invalidTokens.length > 0) {
      return {
        source: null,
        blockedReason: `${invalidTokens.length} linha(s) não são um id válido de cliente — corrija ou remova antes de continuar.`,
      };
    }
    if (ids.length === 0) {
      return { source: null, blockedReason: 'Cole ao menos um id de cliente, um por linha.' };
    }
    if (ids.length > MAX_MANUAL_LEAD_IDS) {
      return {
        source: null,
        blockedReason: `Máximo de ${MAX_MANUAL_LEAD_IDS} ids por vez — remova ${ids.length - MAX_MANUAL_LEAD_IDS} linha(s).`,
      };
    }
    return { source: { type: 'manual', leadIds: ids }, blockedReason: null };
  }

  // 'import' — sem AudienceSource, ver header do arquivo.
  return { source: null, blockedReason: null };
}
